import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AdminLeads from "../components/admin/AdminLeads";
import AdminTestimonials from "../components/admin/AdminTestimonials";
import AdminPortfolio from "../components/admin/AdminPortfolio";
import AdminProfile from "../components/admin/AdminProfile";
import AdminSiteContent from "../components/admin/AdminSiteContent";
import AdminEmailSettings from "../components/admin/AdminEmailSettings";
import AdminFaq from "../components/admin/AdminFaq";
import { LogOut, Inbox, Star, Image as ImageIcon, UserCircle, ExternalLink, Layout, Mail, HelpCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const tabs = [
  { id: "leads", label: "Leads", Icon: Inbox },
  { id: "site", label: "Site", Icon: Layout },
  { id: "email", label: "Email", Icon: Mail },
  { id: "faq", label: "FAQ", Icon: HelpCircle },
  { id: "testimonials", label: "Depoimentos", Icon: Star },
  { id: "portfolio", label: "Portfólio", Icon: ImageIcon },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("leads");
  const { user, profile, logout, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/admin/login");
    }
  }, [user, loading, navigate]);

  if (loading || !user) return null;

  function onLogout() {
    logout();
    navigate("/admin/login");
  }

  return (
    <div className="min-h-screen bg-offwhite">
      <header className="bg-white border-b border-fendi sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-10 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 outline-none hover:opacity-80 transition-opacity" data-testid="admin-user-menu">
                  <Avatar className="h-7 w-7 md:h-8 md:w-8 border border-fendi/20">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="avatar" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <AvatarFallback className="bg-champagne/10 text-softblack text-[9px] md:text-[10px] font-serif uppercase">
                        {profile?.display_name?.charAt(0) || user?.email?.charAt(0) || 'A'}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 mt-1">
                <DropdownMenuLabel className="font-serif font-normal text-warmgray text-xs uppercase tracking-wider">
                  {profile?.display_name || user?.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setTab("profile")} className="cursor-pointer">
                  <UserCircle className="mr-2 h-4 w-4 text-warmgray" />
                  <span className="text-xs uppercase tracking-widest">Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span className="text-xs uppercase tracking-widest">Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/" className="font-serif text-lg md:text-2xl text-softblack" data-testid="admin-logo">
              Hub<span className="text-champagne">.</span>
            </Link>
            
            <nav className="hidden lg:flex gap-2 ml-4" data-testid="admin-tabs">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.28em] uppercase transition-colors ${tab === t.id ? "text-softblack border-b border-champagne" : "text-warmgray hover:text-softblack"}`}
                >
                  <t.Icon size={14} strokeWidth={1.5} />
                  {t.label}
                </button>
              ))}
            </nav>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/" target="_blank" rel="noopener noreferrer" className="text-[10px] lg:text-xs text-warmgray hover:text-softblack" data-testid="admin-view-site">
              <ExternalLink size={10} className="lg:hidden" />
              <span className="hidden lg:inline-flex items-center gap-1 eyebrow"><ExternalLink size={12} /> Ver site</span>
            </Link>
          </div>
        </div>
        <nav className="lg:hidden border-t border-fendi/60 flex w-full">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-mobile-${t.id}`}
              className={`flex items-center justify-center flex-1 min-w-0 py-3 text-[9px] tracking-[0.2em] uppercase ${tab === t.id ? "text-softblack border-t-2 border-champagne" : "text-warmgray"}`}
            >
              <t.Icon size={16} strokeWidth={1.5} />
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-10 py-6 md:py-12">
        {tab === "leads" && <AdminLeads />}
        {tab === "site" && <AdminSiteContent />}
        {tab === "email" && <AdminEmailSettings />}
        {tab === "faq" && <AdminFaq />}
        {tab === "testimonials" && <AdminTestimonials />}
        {tab === "portfolio" && <AdminPortfolio />}
        {tab === "profile" && <AdminProfile />}
      </main>
    </div>
  );
}
